import nikeLogo from "../assets/shoe-brands/nike.svg?url";
import adidasLogo from "../assets/shoe-brands/adidas.svg?url";
import newBalanceLogo from "../assets/shoe-brands/new-balance.svg?url";
import pumaLogo from "../assets/shoe-brands/puma.svg?url";
import reebokLogo from "../assets/shoe-brands/reebok.svg?url";
import converseLogo from "../assets/shoe-brands/converse.svg?url";
import vansLogo from "../assets/shoe-brands/vans.svg?url";
import asicsLogo from "../assets/shoe-brands/asics.svg?url";
import jordanLogo from "../assets/shoe-brands/jordan.svg?url";
import skechersLogo from "../assets/shoe-brands/skechers.svg?url";

export type ShoeBrandId =
  | "nike"
  | "adidas"
  | "new-balance"
  | "puma"
  | "reebok"
  | "converse"
  | "vans"
  | "asics"
  | "jordan"
  | "skechers";

export interface ShoeBrandOption {
  id: ShoeBrandId;
  label: string;
  logoSrc: string;
  hint: string;
  adjustmentCm: number;
}

export type ShoeSizeSystem = "US" | "UK" | "EU" | "JP";
export type ShoeGender = "male" | "female";

export interface ShoeSizeSystemOption {
  value: ShoeSizeSystem;
  label: string;
}

export interface ShoeReferenceEstimate {
  brandId: ShoeBrandId;
  brandLabel: string;
  selectedSize: string;
  selectedSizeSystem: ShoeSizeSystem;
  gender: ShoeGender;
  shoeUS: string;
  shoeUK: string;
  shoeEU: string;
  footLengthCm: number;
  footLengthIn: number;
}

export const SHOE_BRANDS: ShoeBrandOption[] = [
  { id: "nike", label: "Nike", logoSrc: nikeLogo, hint: "Running and lifestyle", adjustmentCm: 0 },
  { id: "adidas", label: "Adidas", logoSrc: adidasLogo, hint: "Sport and streetwear", adjustmentCm: -0.1 },
  { id: "new-balance", label: "New Balance", logoSrc: newBalanceLogo, hint: "Comfort sneakers", adjustmentCm: 0.1 },
  { id: "puma", label: "Puma", logoSrc: pumaLogo, hint: "Sport classics", adjustmentCm: -0.1 },
  { id: "reebok", label: "Reebok", logoSrc: reebokLogo, hint: "Training shoes", adjustmentCm: 0 },
  { id: "converse", label: "Converse", logoSrc: converseLogo, hint: "Canvas classics", adjustmentCm: 0.2 },
  { id: "vans", label: "Vans", logoSrc: vansLogo, hint: "Skate footwear", adjustmentCm: 0.1 },
  { id: "asics", label: "ASICS", logoSrc: asicsLogo, hint: "Performance running", adjustmentCm: 0 },
  { id: "jordan", label: "Jordan", logoSrc: jordanLogo, hint: "Basketball sneakers", adjustmentCm: 0 },
  { id: "skechers", label: "Skechers", logoSrc: skechersLogo, hint: "Everyday comfort", adjustmentCm: 0.2 },
];

const US_SIZE_OPTIONS = [
  "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5",
  "9", "9.5", "10", "10.5", "11", "11.5", "12", "12.5", "13", "13.5", "14",
];
const UK_SIZE_OPTIONS = [
  "3", "3.5", "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5",
  "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12", "12.5", "13",
];
const EU_SIZE_OPTIONS = [
  "35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48",
];
const JP_SIZE_OPTIONS = [
  "22", "22.5", "23", "23.5", "24", "24.5", "25", "25.5", "26", "26.5",
  "27", "27.5", "28", "28.5", "29", "29.5", "30", "30.5",
];

export const SHOE_SIZE_SYSTEM_OPTIONS: ShoeSizeSystemOption[] = [
  { value: "US", label: "United States" },
  { value: "UK", label: "United Kingdom" },
  { value: "EU", label: "Europe" },
  { value: "JP", label: "Japan / CM" },
];

export function getDefaultShoeSizeSystem(country?: string): ShoeSizeSystem {
  const normalized = (country || "").toUpperCase();
  if (["GB", "UK", "IE", "AU", "NZ"].includes(normalized)) return "UK";
  if (["JP", "KR", "CN"].includes(normalized)) return "JP";
  if (["US", "CA"].includes(normalized)) return "US";
  return "EU";
}

export function getShoeSizeOptions(system: ShoeSizeSystem): string[] {
  if (system === "UK") return UK_SIZE_OPTIONS;
  if (system === "EU") return EU_SIZE_OPTIONS;
  if (system === "JP") return JP_SIZE_OPTIONS;
  return US_SIZE_OPTIONS;
}

function formatSize(value: number): string {
  const rounded = Math.round(value * 2) / 2;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

export function inferShoeGender(productTitle?: string, fallbackIsWomen?: boolean): ShoeGender {
  if (/\b(women|woman|womens|women's|ladies|female)\b/i.test(productTitle || "")) return "female";
  if (/\b(men|man|mens|men's|male)\b/i.test(productTitle || "")) return "male";
  return fallbackIsWomen ? "female" : "male";
}

function roundInches(valueCm: number): number {
  return Math.round((valueCm / 2.54) * 10) / 10;
}

function isWomenGender(gender: ShoeGender): boolean {
  return gender === "female";
}

export function formatShoeFootLength(footLengthCm: number, unit: "cm" | "in"): string {
  return unit === "in" ? `${roundInches(footLengthCm)} in` : `${roundOne(footLengthCm)} cm`;
}

function isLikelyWomensShoe(productTitle?: string): boolean {
  return /\b(women|woman|womens|women's|ladies|female)\b/i.test(productTitle || "");
}

export function estimateShoeReference(input: {
  brandId: string;
  size: string;
  sizeSystem: ShoeSizeSystem;
  productTitle?: string;
  gender?: ShoeGender;
}): ShoeReferenceEstimate | null {
  const brand = SHOE_BRANDS.find((item) => item.id === input.brandId);
  const selectedSize = Number.parseFloat(input.size);
  if (!brand || !Number.isFinite(selectedSize) || selectedSize <= 0) return null;

  // Convert the shopper's familiar brand size into a foot-length anchor.
  // US sizing is gendered, so women-specific products use the standard
  // women's-to-men's offset internally. We still show only the shopper's
  // selected scale in the UI, never a combined W/M label.
  const gender = input.gender || (isLikelyWomensShoe(input.productTitle) ? "female" : "male");
  const womenOffset = isWomenGender(gender) ? 1.5 : 0;
  let menEquivalentUs: number;
  let footLengthCm: number;

  if (input.sizeSystem === "UK") {
    menEquivalentUs = selectedSize + 1;
    footLengthCm = roundOne(((menEquivalentUs + 22) / 3) * 2.54 + brand.adjustmentCm);
  } else if (input.sizeSystem === "EU") {
    footLengthCm = roundOne((selectedSize / 1.5) - 1.5 + brand.adjustmentCm);
    menEquivalentUs = (footLengthCm / 2.54) * 3 - 22;
  } else if (input.sizeSystem === "JP") {
    footLengthCm = roundOne(selectedSize + brand.adjustmentCm);
    menEquivalentUs = (footLengthCm / 2.54) * 3 - 22;
  } else {
    menEquivalentUs = selectedSize - womenOffset;
    footLengthCm = roundOne(((menEquivalentUs + 22) / 3) * 2.54 + brand.adjustmentCm);
  }

  const shoeUs = formatSize(menEquivalentUs + womenOffset);
  const shoeUk = formatSize((menEquivalentUs + womenOffset) - (womenOffset > 0 ? 2 : 1));
  const shoeEu = formatSize((footLengthCm + 1.5) * 1.5);

  return {
    brandId: brand.id,
    brandLabel: brand.label,
    selectedSize: formatSize(selectedSize),
    selectedSizeSystem: input.sizeSystem,
    gender,
    shoeUS: shoeUs,
    shoeUK: shoeUk,
    shoeEU: shoeEu,
    footLengthCm,
    footLengthIn: roundInches(footLengthCm),
  };
}
